import {
  GetUserRequest,
  UsersRequest,
  AssignRolesRequest,
  UpdateUserGroupsRequest,
  UpdateFSSAdminCompanyRequest,
  GetUserTicketsRequest,
  UpdateUserProfileRequest,
  UpdateCentralProfileRequest,
  CreateUserRequest,
  SendWelcomeEmailRequest,
  DisableUserRequest,
  ActivateUserRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';
import type { User, CentralUser, NewUserRequest } from '~/users/types';
import type { GroupID } from '~/groups/types';
import type { LngLatLike } from 'mapbox-gl';
import { Location } from '~/tracking/types';
import { RoleID } from '~/roles/types';

export class UserService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new UsersRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    const response = await this.apiClient.request(request);
    const users: User[] = response?.data.map((user: User) => {
      return this.transform(user);
    });

    return { ...response, data: users }; //keep the response to keep meta, override data with transformed users
  }

  async get(userId: string, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetUserRequest({ token: accessToken, params, userId });

    const response = await this.apiClient.request(request);
    return { data: this.transform(response.data) };
  }

  async assignRoles(
    userId: string,
    roles: RoleID[],
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new AssignRolesRequest({
      token: accessToken,
      userId,
      roles,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async updateUserGroups(
    userId: string,
    groupIds: GroupID[],
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateUserGroupsRequest({
      token: accessToken,
      userId,
      groupIds,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async updateFSSCompany(companyId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateFSSAdminCompanyRequest({
      token: accessToken,
      companyId,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async getTickets(userId: string, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetUserTicketsRequest({
      token: accessToken,
      params,
      userId,
    });

    const response = await this.apiClient.request(request);
    return { data: this.transform(response.data) };
  }

  async update(userId: string, user: User, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateUserProfileRequest({
      token: accessToken,
      userId,
      user,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async updateCentral(
    userId: string,
    central: CentralUser,
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateCentralProfileRequest({
      token: accessToken,
      userId,
      central,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async disable(userId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new DisableUserRequest({
      token: accessToken,
      userId,
      params,
    });

    return this.apiClient.request(request);
  }

  async activate(userId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new ActivateUserRequest({
      token: accessToken,
      userId,
      params,
    });

    return this.apiClient.request(request);
  }

  async create(user: NewUserRequest) {
    const accessToken = this.userStore.accessToken;
    const request = new CreateUserRequest({
      token: accessToken,
      user,
    });

    return this.apiClient.request(request);
  }

  async sendWelcomeEmail(userId: string, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new SendWelcomeEmailRequest({
      token: accessToken,
      params,
      userId,
    });

    return this.apiClient.request(request);
  }

  transform(user: User) {
    let gpsLocation: LngLatLike | null = null;
    let lastSeenDate: Date | null = null;
    let transformedLastLocation: { data: Record<string, any> } | undefined =
      undefined;
    let transformedLocation: { data: Record<string, any> } | undefined =
      undefined;

    if (user.lastLocation) {
      const gpsLocationRaw = user.lastLocation.data.gpsLocation;
      gpsLocation = this.transformLocation(gpsLocationRaw as string);
      lastSeenDate = new Date(user.lastLocation.data.created_at);

      transformedLastLocation = {
        data: {
          ...user.lastLocation.data,
          gpsLocation: gpsLocation,
          lastSeenDate: lastSeenDate
            ? useLocaleTimeAgo(lastSeenDate).value
            : null,
        },
      };
    }

    if (user.locations) {
      const locationsNewestFirst = [...user.locations.data].reverse(); //assuming original is oldest first. can't use id anymore- it's hashed

      transformedLocation = {
        data: locationsNewestFirst.map((location: Location) => ({
          ...location,
          gpsLocation: this.transformLocation(location.gpsLocation as string),
        })),
      };
    }

    const transformedUser = {
      ...user,

      //timer property to exist if user.timer was explicitly provided(request sent with include)
      ...(user.timer !== undefined && {
        timer: user.timer,
      }),

      // include if it exists- set to null if it is explicitly null
      ...(user.lastLocation !== undefined && {
        lastLocation: user.lastLocation ? transformedLastLocation : null,
      }),
      ...(user.locations !== undefined && {
        locations: user.locations ? transformedLocation : null,
      }),
    };

    return transformedUser;
  }

  transformLocation(gpsLocationRaw: string) {
    let gpsLocation: LngLatLike | null = null;

    if (
      typeof gpsLocationRaw === 'string' &&
      gpsLocationRaw !== 'DEFAULT' &&
      gpsLocationRaw !== 'N/A'
    ) {
      const location = gpsLocationRaw.split(',');

      if (location.length === 2) {
        gpsLocation = [parseFloat(location[1]), parseFloat(location[0])];
      }
    }
    return gpsLocation;
  }
}
